// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { X as CloseIcon, RefreshCw as RefreshIcon } from 'lucide-react';
import { requestSignedView } from '../../utils/documentViewApi';

// How long before the signed URL expires we silently re-issue. The backend
// returns V4 signed URLs valid for ~15 min; refreshing 60s early keeps the
// iframe alive across the swap without ever showing the user a broken PDF.
const REFRESH_BUFFER_MS = 60 * 1000;

const TITLE_ID = 'pdf-viewer-title';

/**
 * In-app PDF viewer for PHI documents (EOB / LMN). Replaces the previous
 * `window.open(signedUrl, '_blank')` flow so users never leave appealbot.app
 * and we own the signed-URL lifecycle (silent re-issue ~1 min before expiry).
 *
 * The modal:
 *   - calls requestSignedView(documentType, documentId) on open
 *   - renders the returned signed URL inside an iframe (browser's native PDF
 *     viewer — no react-pdf dependency, no sandbox attribute which would
 *     break embedded PDF.js)
 *   - schedules a silent re-fetch at (expires_at - 60s) so a session that
 *     stays open past the 15-min signed-URL TTL keeps working
 *   - clears the timer on close + unmount
 *   - surfaces fetch failures inline with a Try again button rather than
 *     crashing the host page
 *
 * Additive direct-source mode (06-08 letter preview): when `src` is provided
 * the modal skips the signed-URL lifecycle entirely and renders the given URL
 * (e.g. an object URL for a pre-fetched preview blob). The caller owns the
 * URL's lifecycle (revoke on close). `title` optionally overrides the
 * "View {documentLabel}" header.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   documentType?: 'eob' | 'lmn',
 *   documentId?: string,
 *   documentLabel: string,
 *   src?: string|null,
 *   title?: string,
 * }} props
 */
export function PdfViewerModal({
  open,
  onClose,
  documentType,
  documentId,
  documentLabel,
  src = null,
  title,
}) {
  /** @type {[string|null, any]} */
  const [url, setUrl] = useState(/** @type {string|null} */ (null));
  /** @type {[Date|null, any]} */
  const [expiresAt, setExpiresAt] = useState(/** @type {Date|null} */ (null));
  /** @type {[string|null, any]} */
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [loading, setLoading] = useState(false);

  /** @type {{ current: any }} */
  const refreshTimerRef = useRef(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!documentType || !documentId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await requestSignedView(documentType, documentId);
      setUrl(result.url);
      setExpiresAt(result.expires_at ? new Date(result.expires_at) : null);
    } catch (err) {
      const msg = err?.response?.data?.error
        || err?.message
        || 'Could not load document.';
      setError(msg);
      setUrl(null);
      setExpiresAt(null);
    } finally {
      setLoading(false);
    }
  }, [documentType, documentId]);

  // Fetch on open; reset state when closed so reopening starts clean.
  // Direct-source mode: no signed-URL fetch, no expiry timer.
  useEffect(() => {
    if (open && src) {
      setUrl(src);
      setExpiresAt(null);
      setError(null);
      setLoading(false);
    } else if (open && documentId) {
      refresh();
    }
    if (!open) {
      clearRefreshTimer();
      setUrl(null);
      setExpiresAt(null);
      setError(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentType, documentId, src]);

  // Schedule auto-refresh at (expires_at - 60s). If we're already inside the
  // 60s window (or past it), fire immediately so the iframe never holds a
  // stale URL.
  useEffect(() => {
    clearRefreshTimer();
    if (!open || !expiresAt) return undefined;
    const ms = expiresAt.getTime() - Date.now() - REFRESH_BUFFER_MS;
    if (ms <= 0) {
      // Fire on next tick to avoid setState-in-render loops.
      refreshTimerRef.current = setTimeout(() => { refresh(); }, 0);
    } else {
      refreshTimerRef.current = setTimeout(() => { refresh(); }, ms);
    }
    return () => clearRefreshTimer();
  }, [open, expiresAt, refresh, clearRefreshTimer]);

  // Belt-and-suspenders: clear timers on unmount even if `open` never flips.
  useEffect(() => () => {
    clearRefreshTimer();
  }, [clearRefreshTimer]);

  const iframeTitle = `${documentLabel} document viewer`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby={TITLE_ID}
    >
      <DialogTitle
        id={TITLE_ID}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          paddingRight: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
            {title || `View ${documentLabel}`}
          </Typography>
        </Box>
        {!src && (
          <IconButton
            aria-label="Refresh"
            onClick={() => refresh()}
            size="small"
            disabled={loading}
          >
            <RefreshIcon size={18} />
          </IconButton>
        )}
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          padding: 0,
          backgroundColor: '#1a1a1a',
          minHeight: '70vh',
        }}
      >
        {loading && !url && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '70vh',
              backgroundColor: '#fff',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && !loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              minHeight: '70vh',
              padding: 4,
              backgroundColor: '#fff',
              textAlign: 'center',
            }}
          >
            <Typography color="error" variant="body1">
              Could not load document: {error}
            </Typography>
            <Button variant="contained" onClick={() => refresh()}>
              Try again
            </Button>
          </Box>
        )}
        {url && !error && (
          <iframe
            key={url}
            src={url}
            title={iframeTitle}
            style={{
              width: '100%',
              height: '80vh',
              border: 0,
              display: 'block',
              backgroundColor: '#fff',
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PdfViewerModal;
